import { CoreArchiveDialog } from "@/components/core-archive-dialog";
import { useState } from "react";
import { Plus, Search, Download, Filter, Loader2, ArrowUpDown, X, Edit, Archive, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import type { Account, ClientGroup } from "@shared/schema";
import { format } from "date-fns";
import { AccountFormModal } from "@/components/account-form-modal";
import { useUrlState } from "@/hooks/use-url-state";

const ENTITY_TYPE_COLORS = {
  company: "bg-blue-500/10 text-blue-700 dark:text-blue-400",
  trust: "bg-purple-500/10 text-purple-700 dark:text-purple-400",
  partnership: "bg-green-500/10 text-green-700 dark:text-green-400",
  sole_trader: "bg-orange-500/10 text-orange-700 dark:text-orange-400",
};

type SortField = "legalName" | "entityType" | "abn" | "createdAt";
type SortDirection = "asc" | "desc";

export default function AccountList() {
  // Use URL state for filter persistence
  // Search uses debouncing to prevent focus loss on every keystroke
  const [status, setStatus] = useUrlState("status", "active");
  const [searchTerm, setSearchTerm] = useUrlState("search", "", { debounceMs: 300 });
  const [entityTypeFilter, setEntityTypeFilter] = useUrlState("entityType", "");
  const [clientGroupFilter, setClientGroupFilter] = useUrlState("clientGroup", "");
  const [sortField, setSortField] = useUrlState<SortField>("sort", "createdAt");
  const [sortDirection, setSortDirection] = useUrlState<SortDirection>("dir", "desc");

  // Local state (not persisted in URL)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [accountToArchive, setAccountToArchive] = useState<Account | null>(null);

  const [, setLocation] = useLocation();

  const { data: accounts = [], isLoading, isError, error } = useQuery<Account[]>({
    queryKey: ["/api/crm/accounts", { status }],
  });

  // Fetch client groups for filter
  const { data: clientGroups } = useQuery<ClientGroup[]>({
    queryKey: ["/api/crm/client-groups"],
  });

  // Filter accounts
  const filteredAccounts = accounts.filter((account) => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        account.legalName.toLowerCase().includes(searchLower) ||
        account.tradingName?.toLowerCase().includes(searchLower) ||
        account.abn?.toLowerCase().includes(searchLower) ||
        account.industry?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Entity type filter
    if (entityTypeFilter && account.entityType !== entityTypeFilter) {
      return false;
    }

    // Client group filter
    if (clientGroupFilter && account.clientGroupId !== clientGroupFilter) {
      return false;
    }

    return true;
  });

  // Sort accounts
  const displayAccounts = [...filteredAccounts].sort((a, b) => {
    let aValue: string | Date;
    let bValue: string | Date;

    switch (sortField) {
      case "legalName":
        aValue = a.legalName.toLowerCase();
        bValue = b.legalName.toLowerCase();
        break;
      case "entityType":
        aValue = a.entityType;
        bValue = b.entityType;
        break;
      case "abn":
        aValue = (a.abn || "").toLowerCase();
        bValue = (b.abn || "").toLowerCase();
        break;
      case "createdAt":
        aValue = new Date(a.createdAt || 0);
        bValue = new Date(b.createdAt || 0);
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setEntityTypeFilter("");
    setClientGroupFilter("");
  };

  const activeFilterCount = [entityTypeFilter, clientGroupFilter].filter(Boolean).length;

  // Action handlers
  const handleEdit = (account: Account) => {
    setEditingAccount(account);
    setIsModalOpen(true);
  };

  const handleArchive = (account: Account) => {
    setAccountToArchive(account);
    setArchiveDialogOpen(true);
  };

  const handleView = (accountId: string) => {
    setLocation(`/accounts/${accountId}`);
  };



  const handleModalClose = (open: boolean) => {
    setIsModalOpen(open);
    if (!open) {
      setEditingAccount(null);
    }
  };

  return (
    <div className="flex h-screen w-full flex-col">
      <PageHeader
        title="Accounts"
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Accounts" }]}
        actions={
          <Button onClick={() => setIsModalOpen(true)} data-testid="button-add-account">
            <Plus className="mr-2 h-4 w-4" />
            Add Account
          </Button>
        }
      />

      <AccountFormModal
        open={isModalOpen}
        onOpenChange={handleModalClose}
        account={editingAccount ?? undefined}
      />

      <CoreArchiveDialog entity="account" record={accountToArchive} open={archiveDialogOpen}
        onOpenChange={setArchiveDialogOpen} onSuccess={() => setAccountToArchive(null)} />

      <main className="flex-1 overflow-auto bg-muted/20 p-4">
        <div className="mx-auto max-w-7xl space-y-4">
          <Card className="p-3">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by name, ABN, or industry..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-9"
                  data-testid="input-search-accounts"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger aria-label="Account status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
                <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="button-filter-accounts"
                      className={activeFilterCount > 0 ? "bg-accent" : ""}
                    >
                      <Filter className="mr-2 h-4 w-4" />
                      Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80" align="end">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="entity-type-filter" className="text-sm font-medium">
                          Entity Type
                        </Label>
                        <Select value={entityTypeFilter} onValueChange={setEntityTypeFilter}>
                          <SelectTrigger id="entity-type-filter">
                            <SelectValue placeholder="All entity types" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value=" ">All entity types</SelectItem>
                            <SelectItem value="company">Company</SelectItem>
                            <SelectItem value="trust">Trust</SelectItem>
                            <SelectItem value="partnership">Partnership</SelectItem>
                            <SelectItem value="sole_trader">Sole Trader</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="client-group-filter" className="text-sm font-medium">
                          Client Group
                        </Label>
                        <Select value={clientGroupFilter} onValueChange={setClientGroupFilter}>
                          <SelectTrigger id="client-group-filter">
                            <SelectValue placeholder="All client groups" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value=" ">All client groups</SelectItem>
                            {clientGroups?.map((group) => (
                              <SelectItem key={group.id} value={group.id}>
                                {group.groupName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={clearFilters}
                          className="flex-1"
                        >
                          Clear
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => setIsFilterOpen(false)}
                          className="flex-1"
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                <Button variant="outline" size="sm" data-testid="button-export-accounts">
                  <Download className="mr-2 h-4 w-4" />
                  Export
                </Button>
              </div>
            </div>
          </Card>

          <Card>
            {isLoading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : isError ? (
              <div className="flex flex-col items-center justify-center p-8 text-center">
                <p className="text-destructive font-medium">Failed to load accounts</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {error instanceof Error ? error.message : "An error occurred"}
                </p>
              </div>
            ) : filteredAccounts.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-center">
                <p className="text-muted-foreground">No accounts found</p>
                {searchTerm && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Try adjusting your search
                  </p>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="h-10">
                    <TableHead className="px-2 py-1 text-xs font-medium">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 hover:bg-transparent"
                        onClick={() => toggleSort("legalName")}
                      >
                        Legal Name
                        <ArrowUpDown className="ml-2 h-3 w-3" />
                      </Button>
                    </TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium">Trading Name</TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 hover:bg-transparent"
                        onClick={() => toggleSort("entityType")}
                      >
                        Entity Type
                        <ArrowUpDown className="ml-2 h-3 w-3" />
                      </Button>
                    </TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 hover:bg-transparent"
                        onClick={() => toggleSort("abn")}
                      >
                        ABN
                        <ArrowUpDown className="ml-2 h-3 w-3" />
                      </Button>
                    </TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium">Client Group</TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium">Industry</TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 hover:bg-transparent"
                        onClick={() => toggleSort("createdAt")}
                      >
                        Created
                        <ArrowUpDown className="ml-2 h-3 w-3" />
                      </Button>
                    </TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium text-right w-[140px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayAccounts.map((account) => {
                    const clientGroup = clientGroups?.find(g => g.id === account.clientGroupId);
                    return (
                      <TableRow key={account.id} className="h-10 hover:bg-muted/50" data-testid={`row-account-${account.id}`}>
                        <TableCell className="px-2 py-1 text-sm font-medium">
                          <Link href={`/accounts/${account.id}`}>
                            <span className="hover:underline cursor-pointer">
                              {account.legalName}
                            </span>
                          </Link>
                        </TableCell>
                        <TableCell className="px-2 py-1 text-sm text-muted-foreground">
                          {account.tradingName || "-"}
                        </TableCell>
                        <TableCell className="px-2 py-1">
                          <Badge variant="secondary" className={`text-xs capitalize ${ENTITY_TYPE_COLORS[account.entityType as keyof typeof ENTITY_TYPE_COLORS]}`}>
                            {account.entityType.replace("_", " ")}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-2 py-1 text-xs text-muted-foreground font-mono">
                          {account.abn || "-"}
                        </TableCell>
                        <TableCell className="px-2 py-1 text-sm text-muted-foreground">
                          {clientGroup?.groupName || "-"}
                        </TableCell>
                        <TableCell className="px-2 py-1 text-sm text-muted-foreground">
                          {account.industry || "-"}
                        </TableCell>
                        <TableCell className="px-2 py-1 text-sm text-muted-foreground">
                          {account.createdAt ? format(new Date(account.createdAt), "dd/MM/yyyy") : "-"}
                        </TableCell>
                        <TableCell className="px-2 py-1">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(account)}
                              data-testid={`button-edit-account-${account.id}`}
                              title="Edit account"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleArchive(account)}
                              data-testid={`button-archive-account-${account.id}`}
                              title={account.archivedAt ? "Restore account" : "Archive account"}
                            >
                              <Archive className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleView(account.id)}
                              data-testid={`button-view-account-${account.id}`}
                              title="View details"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Card>

          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div>
              Showing {displayAccounts.length} of {accounts.length} accounts
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled data-testid="button-previous-page">
                Previous
              </Button>
              <Button variant="outline" size="sm" disabled data-testid="button-next-page">
                Next
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
